import {setHours, subHours} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import reactTextareaAutosize from 'react-textarea-autosize'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  noBoardContainerId,
  repeatTemplatesContainerId
} from 'yougile-model'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DateRangePicker} from '@components/common/date-picker/date-picker'
import {DropdownList} from '@components/common/dropdown-list/dropdown-list'
import {
  keyboardHandler,
  noPropagate,
  useBoard,
  useBoolean,
  useClickAway
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {UserStickerState} from '@extensions/sticker/views-web/user/user-sticker-state'
import {Switcher} from '../../../common/base/switcher'
import {useCalendarStore} from '../../calendar-store'
import {capitalizeDateFormat, roundMinutes} from '../../utils'
import IconTaskCheckbox from './icon-task-checkbox.svg'

const TwTextArea = tw(reactTextareaAutosize)`
  shadow-none
  focus:shadow-none
  hover:shadow-none
  all-unset
  min-h-64
  overflow-hidden
  cursor-text
  leading-main
  text-main-old
  text-14
  bg-main-old
  w-full
  box-border
  py-6
  focus:border-0
  focus:bg-main-old
`

const TwLabel = tw.div`
  my-4
  mr-8
  leading-4
  text-12
`

const TwWrapper = tw.div`
  flex
  items-baseline
  py-8
`

const TwFilter = tw.div`
  flex-1
  my-4
`

const TwTime = tw.span`
  text-action-default
  cursor-pointer
`

const TwCheckbox = tw.div`
  inline-flex
  items-center
  shrink-0
  pb-24
  cursor-pointer
  w-220
`

const straightLine = (
  <div
    className="h-1"
    style={{
      background: `repeating-linear-gradient(to right, #C4C4C4 0, #C4C4C4 3px, transparent 3px)`
    }}
  />
)

const dateFormat = 'dd LLL yyyy HH:mm'
const DateFormatAllDay = 'dd LLL yyyy'

interface Props {
  start: Date
  end: Date
  allDay?: boolean
}

// eslint-disable-next-line max-lines-per-function
export const AddTask = observer(({start, end, allDay = false}: Props) => {
  const [state, store] = useCalendarStore()
  useEffect(() => {
    store.setAddTaskWithStartDate(!allDay)
  }, [state.openedAllDayPopup, state.openedPopup])
  const format = allDay ? DateFormatAllDay : dateFormat
  const allowClickOutside = useBoolean(true)
  const showDatePicker = useBoolean(false)
  const ref = useRef<HTMLInputElement | null>(null)
  const language = Lang.getLang()
  const board = useBoard()
  const [selectedColumn, selectColumn] = useState<DataObjectId>()
  const [assignedIds, selectAssigned] = useState<DataObjectId[]>([])
  const options = state.checkCols
    .filter(el => el.checkAddTask)
    .map(x => ({
      label: x.col.title,
      value: x.col.id
    }))
  useEffect(() => {
    if (options.length) {
      selectColumn(options[0].value)
      store.setStartDatePopup(roundMinutes(start))
      store.setEndDatePopup(roundMinutes(end))
    }
  }, [])
  const escape = () => {
    store.setOpenedAllDayPopup(false)
    store.setOpenedPopup(false)
    store.clearDatePopup()
  }
  useClickAway(ref, () => {
    if (allowClickOutside.value) {
      if (state.newTaskTitle.length > 0) {
        createTask()
      } else {
        escape()
      }
    }
  })
  const value = state.checkCols.find(x => x.col.id === selectedColumn)
  const isRepeatTemplate = board.location === repeatTemplatesContainerId
  const box = Sync.getCurrentSandbox()
  const allUsers = box
    .getAllUsers(false)
    .map(u => u.id)
    .filter(uid => {
      const repeatCol = isRepeatTemplate
        ? box.getHub(board.data?.repeatTemplate?.columnId)
        : undefined
      const noBoard = board.location === noBoardContainerId
      return (
        noBoard ||
        PermissionsChecker.checkShow('ignore-departments', repeatCol ?? board, {
          considerAsMy: true,
          considerAsWithMe: true,
          userId: uid,
          ignoreAdmin: true
        })
      )
    })
  const createTask = () => {
    const withTime = !allDay
    const startDate = allDay
      ? setHours(state.startDatePopup, 12)
      : state.startDatePopup
    const endDate = allDay
      ? setHours(state.endDatePopup, 12)
      : state.endDatePopup

    store.createNewTask(
      selectedColumn,
      state.addTaskWithStartDate
        ? {
            withTime,
            assignedIds,
            startDate,
            endDate
          }
        : {
            withTime,
            assignedIds,
            endDate
          }
    )
    escape()
  }
  const onToggleTime = () => {
    showDatePicker.toggle()
    window.dispatchEvent(new Event('resize'))
  }
  if (options.length === 0) {
    return (
      <div className="p-8 text-warning-old" ref={ref}>
        {Lang.msg.calendar.noColumn}
      </div>
    )
  }

  return (
    <div className="flex" ref={ref}>
      <div className="mt-3 text-input-disabled-old">
        <IconTaskCheckbox />
      </div>
      <div className="flex-col text-main-old">
        <div
          className={cn(
            !state.addTaskWithStartDate && 'mr-56',
            !showDatePicker.value && 'mr-56'
          )}
        >
          <TwTextArea
            autoFocus
            onKeyDown={keyboardHandler({
              enter: () => {
                createTask()
                return true
              },
              escape: () => {
                escape()
                return true
              }
            })}
            onMouseDown={noPropagate}
            value={state.newTaskTitle}
            onChange={e => {
              store.setNewTaskTitle(e.target.value)
            }}
            placeholder={Lang.msg.enterTaskName}
          />
        </div>

        {straightLine}

        <div
          className={cn(
            !state.addTaskWithStartDate && 'mr-56',
            !showDatePicker.value && 'mr-56'
          )}
        >
          <div className="flex py-14">
            <div className="mr-12 leading-4 text-12">{Lang.msg.assigned}:</div>
            <UserStickerState
              users={assignedIds}
              onChoose={selectAssigned}
              options={allUsers}
              title={Lang.msg.assigned}
              onOpenChange={isOpen => {
                allowClickOutside.setValue(!isOpen)
              }}
            />
          </div>

          <TwCheckbox
            onClick={() => {
              store.setStartDatePopup(subHours(state.endDatePopup, 1))
              store.setAddTaskWithStartDate(!state.addTaskWithStartDate)
            }}
          >
            <div className="mr-12 leading-4 text-12">
              {Lang.msg.calendar.withStartDate}
            </div>
            <Switcher active={state.addTaskWithStartDate} />
          </TwCheckbox>

          <div className="pb-18">
            {state.addTaskWithStartDate ? (
              <div className="leading-4 text-12">
                {Lang.msg.calendar.from}{' '}
                <TwTime onClick={onToggleTime}>
                  {capitalizeDateFormat(state.startDatePopup, language, format)}
                </TwTime>{' '}
                {Lang.msg.calendar.to.toLowerCase()}{' '}
                <TwTime onClick={onToggleTime}>
                  {capitalizeDateFormat(state.endDatePopup, language, format)}
                </TwTime>
              </div>
            ) : (
              <div className="leading-4 text-12">
                {Lang.msg.calendar.to}{' '}
                <TwTime onClick={onToggleTime}>
                  {capitalizeDateFormat(state.endDatePopup, language, format)}
                </TwTime>
              </div>
            )}
            {showDatePicker.value && (
              <div className="-ml-24">
                <DateRangePicker
                  showTimeInput={!allDay}
                  value={
                    state.addTaskWithStartDate
                      ? {
                          start: state.startDatePopup,
                          end: state.endDatePopup
                        }
                      : {
                          end: state.endDatePopup
                        }
                  }
                  onChange={date => {
                    if (state.addTaskWithStartDate) {
                      store.setStartDatePopup(date.start)
                      store.setEndDatePopup(date.end)
                    } else {
                      store.setStartDatePopup(date.end)
                      store.setEndDatePopup(date.end)
                    }
                  }}
                  hideRangeSection={
                    state.addTaskWithStartDate ? undefined : 'start'
                  }
                  onOpenChange={isOpen => {
                    allowClickOutside.setValue(!isOpen)
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {straightLine}

        <TwWrapper
          className={cn(
            !state.addTaskWithStartDate && 'mr-56',
            !showDatePicker.value && 'mr-56'
          )}
        >
          <TwLabel>{Lang.msg.calendar.location}:</TwLabel>
          <TwFilter ref={ref}>
            <DropdownList
              className="w-160"
              initialOpen={false}
              options={options}
              value={value?.col.id}
              onChange={selectColumn}
              onOpenChange={isOpen => {
                allowClickOutside.setValue(!isOpen)
              }}
            />
          </TwFilter>
        </TwWrapper>
      </div>
    </div>
  )
})
